import { Divider, Link, Stack, Tooltip, Typography, Box } from '@mui/material';
import { useAppDispatch, useForm } from '../../../../../hooks';
import { GoogleSigninButton } from '../../components/GoogleSigninButton';
import { ElamaSigninButton } from '../../components/ElamaSigninButton';
import { FormButton, FormInput } from '../../../../UI';
import PasswordTooltip from '../../components/PasswordTooltip';
import { useState } from 'react';
import { translateErrorMessage, validateEmail, validatePassword } from '../../../../../utils/authHelpers';
import { INCORRECT_EMAIL, INCORRECT_PASSWORD } from '../../../../../constants/validationErrorMessages';
import { AUTH_API } from '../../../../../api/amplify';
import { AuthForm, User } from '../../../../../types/auth';
import { setAuthForm, setCognitoUserEmail, setCognitoUserPassword } from '../../../../../store/CognitoSlice';

import { showAlert } from '../../../../../store';
import { AlertType } from '../../../../../types/common';
import { CODE_SENT } from '../../../../../constants/authMessages';

const INIT_USER: User = {
  password: '',
  email: '',
};

declare global {
  interface Window {
    ymGlobal: any;
  }
}

export const Signup = () => {
  const dispatch = useAppDispatch();

  const { input, error, handleChange, submit, handlePressEnter, setError, resetForm } = useForm(
    INIT_USER,
    handleSubmit
  );

  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit() {
    try {
      if (!validatePassword(input.password)) {
        setError((prev) => ({
          ...prev,
          password: INCORRECT_PASSWORD,
        }));
        return;
      }

      if (!validateEmail(input.email)) {
        setError((prev) => ({
          ...prev,
          email: INCORRECT_EMAIL,
        }));
        return;
      }

      setIsLoading(true);

      const { email, password } = input;

      await AUTH_API.signUp(email, password, email);
      dispatch(showAlert({ type: AlertType.Success, message: CODE_SENT }));

      dispatch(setCognitoUserEmail(email));
      dispatch(setCognitoUserPassword(password));
      dispatch(setAuthForm(AuthForm.ConfirmEmail));

      resetForm();
    } catch (e: any) {
      const errorMessage = translateErrorMessage(e);
      dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
    }
    setIsLoading(false);

    if (window.ymGlobal) {
      window.ymGlobal(90338500, 'reachGoal', 'sign_up');
    }
  }

  const switchToSigninForm = () => {
    dispatch(setAuthForm(AuthForm.Signin));
  };

  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        gap: '60px',
      }}
    >
      <Stack onKeyDown={handlePressEnter}>
        <Typography
          mb={'30px'}
          align={'center'}
          sx={{
            typography: {
              mobile: 'h3',
              laptop: 'h1',
            },
          }}
        >
          Создать аккаунт
        </Typography>

        <Stack gap={'20px'} component={'form'} onSubmit={submit}>
          <GoogleSigninButton />
          <ElamaSigninButton />
          <Box>
            <Divider light>
              <Typography variant="subtitle1">или</Typography>
            </Divider>
          </Box>

          <Box>
            <FormInput
              onChange={handleChange}
              error={error?.email || ''}
              name={'email'}
              value={input.email || ''}
              placeholder={'Email'}
              label={'Email'}
              smallLabel
            />
            <Tooltip
              title={<PasswordTooltip password={input.password} />}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    padding: 0,
                    boxShadow:
                      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
                  },
                },
              }}
              disableHoverListener
              placement={'right-end'}
            >
              <Stack sx={{}}>
                <FormInput
                  onChange={handleChange}
                  error={error?.password || ''}
                  name={'password'}
                  value={input.password || ''}
                  placeholder={'Пароль'}
                  label={'Пароль'}
                  smallLabel
                  type={'password'}
                />
              </Stack>
            </Tooltip>
          </Box>
          <Stack gap={'30px'}>
            <FormButton submit isLoading={isLoading}>
              Зарегистрироваться
            </FormButton>
            <Box component={'span'}>
              <Typography variant="subtitle1" component={'span'}>
                Уже зарегистрированы?{' '}
              </Typography>

              <Link component={'button'} onClick={switchToSigninForm} underline={'none'} type={'button'}>
                Войти
              </Link>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
